import React, { useEffect, useRef, useState } from 'react';
import { jsonEditorStyles } from '../jsonEditorStyles';
import Button from '@mui/material/Button';
import cn from 'clsx';
import useComponentUIHook from './useComponentUIHook';
import jsonComponents from '../../../constants/jsonComponents';
import { OptionProps } from '../../../view-models';
import { SelectComponent, TextComponent, TooltipComponent } from '../../uiComponents';
import { SelectChangeEvent } from '@mui/material';
import { cloneDeep } from 'lodash';
import TemplatizedFieldComponent, { getTemplatizedValue, TemplatizedValue } from './templatizedFieldComponent';

interface componentProps {
  componentData: any;
  closeGritComponent: any;
  setUnsavedChanges: any;
}

const GenerateAuthorizeTokenUI = ({ componentData, closeGritComponent, setUnsavedChanges }: componentProps) => {
  const { modifiedData, setModifiedData, checkIfSameIgnoringSubFlows, allClaims, modifiedTemplatizations, setModifiedTemplatizations } = useComponentUIHook(componentData);
  const configData = jsonComponents.authorizeToken;
  const [claimsList, setClaimsList] = useState<OptionProps[]>([]);
  const [disableSubmit, setDisableSubmit] = useState<boolean>(false);
  const [nextClaim, setNextClaim] = useState<any>({
    id: '',
    partnerClaimType: '',
  });

  const classes = jsonEditorStyles();

  const submitData = (data: any) => {
    setDisableSubmit(false);
    closeGritComponent(data, modifiedTemplatizations, false);
  };

  const onDeleteHandler = (index: number) => {
    const _claims = modifiedData['claims'].reduce((acc: any[], item: any, ind: number) => {
      return ind === index ? acc : acc.concat(item);
    }, []);
    setModifiedData({
      ...modifiedData,
      claims: _claims,
    });
  };

  useEffect(() => {
    if (Object.keys(allClaims).length > 0) {
      const _temp = Object.keys(allClaims)
        .reduce((acc: OptionProps[], curVal: string) => acc.concat({ label: curVal, value: curVal }), [])
        .sort((a: OptionProps, b: OptionProps) => (a.value.toLowerCase() > b.value.toLowerCase() ? 1 : -1));
      setClaimsList(_temp);
    }
  }, [allClaims]);

  useEffect(() => {
    const _res: boolean = checkIfSameIgnoringSubFlows();
    setDisableSubmit(
      _res ||
        modifiedData['claims'].some((clm: any) => {
          return !clm.id;
        }),
    );
    setUnsavedChanges(_res);
  }, [modifiedData]);

  return (
    <div>
      <TemplatizedFieldComponent
        modifiedTemplatizations={modifiedTemplatizations}
        setModifiedTemplatizations={setModifiedTemplatizations}
        data={{
          label: 'Audience Application ID',
          required: true,
          fieldPath: 'oidcApplicationId',
          style: { width: 600, margin: 1 },
          value: getTemplatizedValue(modifiedData['oidcApplicationId']),
          variant: 'text'
        }}
        changeHandler={(e: TemplatizedValue) => {
          setModifiedData({
            ...modifiedData,
            oidcApplicationId: { ...e }
          })
        }}
      />
      <div className={classes.subHeading}>Select claims to extract from authorization token</div>
      {modifiedData['claims'] &&
        modifiedData['claims'].map((ent: any, idx: number) => {
          return (
            <div key={`claimEntry${idx}`} className={classes.flexRow}>
              <SelectComponent
                changeHandler={(e: SelectChangeEvent<string>) => {
                  const updatedClaims = modifiedData['claims'].slice();
                  updatedClaims[idx] = { id: e.target.value, partnerClaimType: updatedClaims[idx]['partnerClaimType'] };
                  setModifiedData({
                    ...modifiedData,
                    claims: updatedClaims,
                  });
                }}
                data={{
                  value: modifiedData['claims'][idx]['id'] ? modifiedData['claims'][idx]['id'] : '',
                  label: 'Claim',
                  options: claimsList.filter((clm) => {
                    return clm.value === modifiedData['claims'][idx]?.id || !modifiedData['claims'].includes(clm.value);
                  }),
                  style: { margin: 1, width: 200 },
                }}
              />
              <TextComponent
                changeHandler={(e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
                  const updatedClaims = modifiedData['claims'].slice();
                  updatedClaims[idx] = { id: updatedClaims[idx]['id'], partnerClaimType: e.target.value };
                  setModifiedData({
                    ...modifiedData,
                    claims: updatedClaims,
                  });
                }}
                data={{
                  value: modifiedData['claims'][idx]['partnerClaimType']
                    ? modifiedData['claims'][idx]['partnerClaimType']
                    : '',
                  type: 'string',
                  label: 'Token Claim Name',
                  style: { margin: 1, width: 250 },
                }}
              />
              <Button
                variant="contained"
                sx={{ alignSelf: 'center' }}
                onClick={() => {
                  onDeleteHandler(idx);
                }}
              >
                Delete
              </Button>
            </div>
          );
        })}
      <div className={classes.flexRow}>
        <SelectComponent
          changeHandler={(e: SelectChangeEvent<string>) => {
            setNextClaim({
              id: e.target.value,
              partnerClaimType: nextClaim.partnerClaimType,
            });
          }}
          data={{
            value: nextClaim['id'] ? nextClaim['id'] : '',
            label: 'Claim',
            options: claimsList.filter((clm) => {
              return !modifiedData['claims'].includes(clm.value);
            }),
            style: { margin: 1, width: 200 },
          }}
        />
        <TextComponent
          changeHandler={(e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
            setNextClaim({
              id: nextClaim['id'],
              partnerClaimType: e.target.value,
            });
          }}
          data={{
            value: nextClaim['partnerClaimType'] ? nextClaim['partnerClaimType'] : '',
            type: 'string',
            label: 'Token Claim Name',
            style: { margin: 1, width: 250 },
          }}
        />
        <Button
          variant="contained"
          sx={{ alignSelf: 'center' }}
          disabled={!nextClaim.id}
          onClick={() => {
            const updatedClaims = modifiedData['claims'].slice();
            updatedClaims.push({ id: nextClaim.id, partnerClaimType: nextClaim.partnerClaimType });
            setNextClaim({ id: '', partnerClaimType: '' });
            setModifiedData({
              ...modifiedData,
              claims: updatedClaims,
            });
          }}
        >
          Add
        </Button>
      </div>
      <div>
        <Button
          type="submit"
          fullWidth
          variant="contained"
          sx={{ mt: 3, mb: 2 }}
          onClick={() => {
            submitData(cloneDeep(modifiedData));
          }}
          disabled={disableSubmit}
        >
          Save
        </Button>
      </div>
    </div>
  );
};

export default GenerateAuthorizeTokenUI;
